/*
 * The form screen's side of comments: which thread is open, the draft field,
 * the Open/Resolved filter, the page's pins and the `?field=&comment=` deep
 * link. The screen owns the panel and the selection; this decides what a pin,
 * a Comment action or a link opens, as the comments canvas does: a field with
 * an open thread opens it, otherwise a new thread starts on the field.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  commentPinsByField,
  type CommentPinInfo,
  type FormCommentFilter
} from "../../commentsModel";
import { useCommentDeepLink } from "../../hooks/useCommentDeepLink";
import { useFormComments } from "../../hooks/useFormComments";
import { CommentPin } from "../CommentPin";
import type { FormCanvasFieldOverlayProps } from "../FormCanvas";

export type ShowComments = (
  fieldName: string,
  options: { scroll: boolean }
) => void;

export function useFormCommentsBridge({
  individualId,
  filledFormId,
  paneShown,
  show
}: {
  individualId: string;
  filledFormId: string;
  /** The side panel is open on its Comments tab. */
  paneShown: boolean;
  /** Open the Comments tab and select the field (scrolling to it when asked). */
  show: ShowComments;
}) {
  const comments = useFormComments(individualId, filledFormId);
  const { threads } = comments;
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [draftFieldName, setDraftFieldName] = useState<string | null>(null);
  const [filter, setFilter] = useState<FormCommentFilter>("open");
  const showRef = useRef(show);
  showRef.current = show;

  const link = useCommentDeepLink();
  /** A linked thread whose field the link didn't name: shown once it loads. */
  const pendingLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!link.commentId && !link.fieldName) return;
    if (link.commentId) setActiveThreadId(link.commentId);
    if (link.fieldName) showRef.current(link.fieldName, { scroll: true });
    else pendingLinkRef.current = link.commentId;
    // Once, on mount.
  }, []);
  useEffect(() => {
    const pending = pendingLinkRef.current;
    const thread = pending ? threads.find((t) => t.id === pending) : null;
    if (!thread) return;
    pendingLinkRef.current = null;
    showRef.current(thread.field_name, { scroll: true });
  }, [threads]);

  /** A pin: open its thread under its own filter. */
  const openThread = (threadId: string) => {
    const thread = threads.find((t) => t.id === threadId);
    if (!thread) return;
    setDraftFieldName(null);
    setFilter(thread.resolved ? "resolved" : "open");
    setActiveThreadId(thread.id);
    show(thread.field_name, { scroll: false });
  };

  /** The hover card's or a source row's Comment. */
  const commentOn = (fieldName: string) => {
    const open = threads.find((t) => t.field_name === fieldName && !t.resolved);
    setFilter("open");
    setActiveThreadId(open?.id ?? null);
    setDraftFieldName(open ? null : fieldName);
    show(fieldName, { scroll: false });
  };

  const pinFilter: FormCommentFilter =
    paneShown && filter === "resolved" ? "resolved" : "open";
  const pins = useMemo(
    () => commentPinsByField(threads, pinFilter),
    [threads, pinFilter]
  );

  return {
    counts: comments.counts,
    pins,
    activeThreadId,
    setActiveThreadId,
    draftFieldName,
    clearDraft: () => setDraftFieldName(null),
    filter,
    setFilter,
    openThread,
    commentOn
  };
}

/** The pin at a field's top-right corner (first box only); null without threads. */
export function renderCommentPin(
  { field, boxIndex, rect }: FormCanvasFieldOverlayProps,
  pin: CommentPinInfo | undefined,
  onOpen: (threadId: string) => void
): React.ReactNode {
  if (!pin || boxIndex !== 0) return null;
  return (
    <CommentPin
      key={`pin-${field.field_name}`}
      pin={pin}
      onOpen={onOpen}
      className="pointer-events-auto absolute z-10"
      style={{ left: rect.left + rect.width - 8, top: rect.top - 10 }}
    />
  );
}
